import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { authErrorKeys } from 'src/app/util/auth-error.util';
import { passwordProblems, isMismatch } from '../../../util/password-form.util';

type Stage = 'password' | 'invalid' | 'done';

/**
 * Completes a password reset from an emailed link.
 *
 * On success it does NOT sign the user in. The reset deliberately leaves the
 * second factor untouched server-side -- so mailbox access alone is not a
 * takeover -- which means the next sign-in still has to pass MFA. Dropping the
 * user at the login page is therefore the honest end of this flow, and the copy
 * says so.
 */
@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['../auth-shell.css'],
    standalone: false
})
export class ResetPasswordComponent implements OnInit {
  stage: Stage = 'password';
  busy = false;
  errorKeys: string[] = [];

  token = '';
  password = '';
  repeat = '';
  minLength = 12;

  constructor(private route: ActivatedRoute, private auth: AuthService) {}

  ngOnInit() {
    // Same reasoning as accept-invitation: do not let the interceptor attach a
    // stale bearer token, and do not show logged-in chrome on an anonymous page.
    this.auth.clearLocalStorage();

    this.route.queryParams.subscribe((params) => {
      this.token = params['token'] || '';
      if (!this.token) { this.stage = 'invalid'; return; }
      // The minimum is advisory here -- the server is authoritative and will
      // refuse with a policy code if this is out of date.
      this.auth.providers$().subscribe({
        next: (p) => { this.minLength = p.passwordMinLength || 12; },
        error: () => { /* keep the default */ }
      });
    });
  }

  get mismatch(): boolean {
    return isMismatch(this.password, this.repeat);
  }

  /**
   * Only `busy` disables the button.
   *
   * It used to also require the length and match rules, which made a valid-looking
   * form produce a button that did nothing and said nothing: the length rule was
   * stated as a note, never as an error, so a short password gave no feedback at
   * all. "I click save and nothing happens" was exactly that.
   *
   * Validation now happens on submit and names the problem. A button you can
   * press and be told why is strictly better than one you cannot press for
   * reasons you must infer.
   */
  get canSubmit(): boolean {
    return !this.busy;
  }

  /** Client-side rules, as translation keys. Empty means submit. */
  private localProblems(): string[] {
    return passwordProblems({
      password: this.password,
      repeat: this.repeat,
      minLength: this.minLength,
      mismatchShown: this.mismatch
    });
  }

  submit() {
    if (this.busy) return;
    // Report every broken rule at once rather than one per attempt.
    const problems = this.localProblems();
    if (problems.length) { this.errorKeys = problems; return; }
    this.busy = true;
    this.errorKeys = [];
    this.auth.resetPassword(this.token, this.password).subscribe({
      next: () => {
        this.busy = false;
        this.password = '';
        this.repeat = '';
        this.stage = 'done';
      },
      error: (err) => { this.busy = false; this.errorKeys = authErrorKeys(err); }
    });
  }
}
